import { gql, useLazyQuery } from '@apollo/client';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CancelIcon from '@mui/icons-material/Cancel';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import DownloadIcon from '@mui/icons-material/Download';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  IconButton,
  Link,
  LinearProgress as MuiLinearProgress,
  Typography as MuiTypography,
  TextField,
} from '@mui/material';
import { Box } from '@ovComponents/1-primative/box/box';
import { useGlobalToast } from '@providers/globalToastProvider';
import { useThemeTokens } from '@providers/themeTokenProvider';
import { validateCsvFile } from '@utils/csvFileValidation';
import { safeWindowOpen } from '@utils/urlSanitization';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../button/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTitle,
} from '../dialog/dialog';
import { DroppableFileInput } from '../droppableFileInput/droppableFileInput';
import { FileTypes } from '../droppableFileInput/fileTypes';

const ACCEPTED_TYPES: FileTypes[] = [FileTypes.CSV];
const COMPLETE_PROGRESS = 100;

const CSV_IMPORT_ERRORS = {
  EMPTY_PASTE: 'bulkImport:errors.emptyPaste',
  INVALID_CONTENT: 'bulkImport:errors.invalidContent',
  PROCESS_FAILED: 'bulkImport:errors.processFailed',
  UNKNOWN_ERROR: 'bulkImport:errors.unknownError',
} as const;

const FETCH_TEMPLATE_DOWNLOAD_URL = gql`
  query fetchImportFileTemplate($importFileType: ImportFileTypes!) {
    fetchImportFileTemplate(importFileType: $importFileType) {
      downloadUrl
    }
  }
`;

export type ImportCsvModalProps = {
  open: boolean;
  onClose: () => void;
  onImport: (
    file: File,
    onProgress?: (progress: number, message?: string) => void,
  ) => Promise<void>;
  importFileType: string;
  title?: string;
  description?: string;
  importButtonLabel?: string;
  successMessage?: string;
  errorMessage?: string;
  testIdPrefix?: string;
};

export const ImportCsvModal = ({
  open,
  onClose,
  onImport,
  importFileType,
  title,
  description,
  importButtonLabel,
  successMessage,
  errorMessage,
  testIdPrefix,
}: ImportCsvModalProps) => {
  const { t } = useTranslation(['shared', 'components', 'bulkImport']);
  const { sys } = useThemeTokens();
  const [file, setFile] = useState<File | undefined>();
  const [uploadPending, setUploadPending] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showPasteDialog, setShowPasteDialog] = useState(false);
  const [pastedText, setPastedText] = useState('');
  const { showToast } = useGlobalToast();
  const [fetchTemplateDownloadUrl] = useLazyQuery(FETCH_TEMPLATE_DOWNLOAD_URL, {
    fetchPolicy: 'no-cache',
  });

  const handleClose = useCallback(() => {
    if (!uploadPending) {
      setFile(undefined);
      setUploadProgress(0);
      onClose();
    }
  }, [uploadPending, onClose]);

  const downloadTemplate = useCallback(async () => {
    const queryResult = await fetchTemplateDownloadUrl({
      variables: { importFileType },
    });
    const templateUrl = queryResult?.data?.fetchImportFileTemplate?.downloadUrl;
    if (!templateUrl || queryResult?.error) {
      showToast({
        severity: 'error',
        message: t('bulkImport:errorFetchingTemplate', {
          error: queryResult?.error,
        }),
      });
      return;
    }
    if (window && templateUrl) {
      setTimeout(() =>
        safeWindowOpen(templateUrl, {
          target: '_blank',
          onError: (errorMsg) => {
            showToast({ severity: 'error', message: errorMsg });
          },
        }),
      );
    }
  }, [fetchTemplateDownloadUrl, importFileType, showToast, t]);

  const removeFile = useCallback(() => {
    setFile(undefined);
    setUploadProgress(0);
  }, []);

  const handleProgress = useCallback((progress: number, _message?: string) => {
    setUploadProgress(Math.min(progress, COMPLETE_PROGRESS));
  }, []);

  const handleSubmitWithFile = useCallback(
    async (fileToImport: File) => {
      try {
        setUploadPending(true);
        setUploadProgress(0);

        await onImport(fileToImport, handleProgress);

        setUploadProgress(COMPLETE_PROGRESS);

        showToast({
          severity: 'success',
          message:
            successMessage || `${fileToImport.name} imported successfully`,
        });
        setFile(undefined);
        setUploadProgress(0);
        onClose();
      } catch (error) {
        const errorMsg = error instanceof Error ? error.message : String(error);
        showToast({
          severity: 'error',
          message: errorMessage
            ? `${errorMessage}: ${errorMsg}`
            : `Import failed: ${errorMsg}`,
        });
        setUploadProgress(0);
      } finally {
        setUploadPending(false);
      }
    },
    [
      onImport,
      handleProgress,
      successMessage,
      showToast,
      onClose,
      errorMessage,
    ],
  );

  const handlePasteSubmit = useCallback(async () => {
    if (!pastedText.trim()) {
      showToast({
        severity: 'error',
        message: t(CSV_IMPORT_ERRORS.EMPTY_PASTE, {
          defaultValue: 'Please paste CSV content',
        }),
      });
      return;
    }

    try {
      const blob = new Blob([pastedText], { type: 'text/csv' });
      const csvFile = new File([blob], 'pasted-data.csv', { type: 'text/csv' });

      const validationResult = await validateCsvFile(csvFile);
      if (!validationResult.valid) {
        showToast({
          severity: 'error',
          message: t(CSV_IMPORT_ERRORS.INVALID_CONTENT, {
            defaultValue: 'Invalid CSV content: {{error}}',
            error:
              validationResult.error ||
              t(CSV_IMPORT_ERRORS.UNKNOWN_ERROR, {
                defaultValue: 'Unknown error',
              }),
          }),
        });
        return;
      }

      setShowPasteDialog(false);
      setPastedText('');
      setFile(csvFile);

      await handleSubmitWithFile(csvFile);
    } catch {
      showToast({
        severity: 'error',
        message: t(CSV_IMPORT_ERRORS.PROCESS_FAILED, {
          defaultValue: 'Failed to process pasted content',
        }),
      });
    }
  }, [pastedText, showToast, t, handleSubmitWithFile]);

  const handleSubmit = useCallback(async () => {
    if (!file) return;
    await handleSubmitWithFile(file);
  }, [file, handleSubmitWithFile]);

  const handleFileChosen = useCallback((dropFile: File) => {
    setFile(dropFile);
  }, []);

  const openPasteDialog = useCallback(() => {
    setShowPasteDialog(true);
  }, []);

  const closePasteDialog = useCallback(() => {
    setShowPasteDialog(false);
    setPastedText('');
  }, []);

  const handlePastedTextChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setPastedText(e.target.value);
    },
    [],
  );

  const prefix = testIdPrefix ? `${testIdPrefix}-` : '';
  const modalTestId = `${prefix}import-csv-modal`;
  const cancelButtonTestId = `${prefix}import-csv-cancel-button`;
  const submitButtonTestId = `${prefix}import-csv-submit-button`;
  const dropZoneTestId = `${prefix}import-csv-drop-zone`;
  const copyPasteTestId = `${prefix}import-csv-copy-paste-button`;
  const removeFileTestId = `${prefix}import-csv-remove-file-button`;
  const downloadTemplateTestId = `${prefix}import-csv-download-template-link`;
  const progressBarTestId = `${prefix}import-csv-progress-bar`;
  const pasteDialogTestId = `${prefix}paste-csv-dialog`;
  const pasteDialogTextareaTestId = `${prefix}paste-csv-textarea`;
  const pasteDialogDownloadTemplateTestId = `${prefix}paste-csv-download-template-link`;
  const pasteDialogCancelTestId = `${prefix}paste-csv-cancel-button`;
  const pasteDialogImportTestId = `${prefix}paste-csv-import-button`;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth='sm'
      fullWidth
      data-testid={modalTestId}
    >
      <DialogTitle onClose={handleClose}>
        {title || t('shared:importCsv')}
      </DialogTitle>
      <DialogContent>
        <Box display='flex' flexDirection='column' gap={2}>
          {description && !file && !uploadPending && (
            <MuiTypography variant='body1' sx={{ fontSize: '16px' }}>
              {description}
            </MuiTypography>
          )}

          {!file && !uploadPending && (
            <Box
              sx={{
                mb: 1,
                p: 2,
                backgroundColor: sys.color.surfaceContainerVariant,
                borderRadius: sys.borderRadius.md,
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1.5,
              }}
            >
              <InfoOutlinedIcon
                fontSize='small'
                sx={{
                  color: sys.color.onSurfaceVariant,
                }}
              />
              <MuiTypography
                variant='body2'
                component='span'
                sx={{ color: sys.color.onSurface }}
              >
                {t('bulkImport:notSureHowToFormat')}{' '}
                <Link
                  onClick={downloadTemplate}
                  sx={{ cursor: 'pointer' }}
                  data-testid={downloadTemplateTestId}
                >
                  {t('bulkImport:template')}
                </Link>
              </MuiTypography>
            </Box>
          )}

          {/* File upload area or file display */}
          {!file && !uploadPending && (
            <DroppableFileInput
              acceptedTypes={ACCEPTED_TYPES}
              onFileChosen={handleFileChosen}
              testId={dropZoneTestId}
            />
          )}

          {/* File attached view */}
          {file && !uploadPending && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                p: 1.5,
                border: `1px solid ${sys.color.outline}`,
                borderRadius: sys.borderRadius.md,
                backgroundColor: sys.color.surface,
              }}
            >
              <Box display='flex' alignItems='center' gap={1.5}>
                <AttachFileIcon
                  sx={{
                    color: sys.color.onSurfaceVariant,
                    fontSize: '18px',
                  }}
                />
                <MuiTypography variant='body2'>{file.name}</MuiTypography>
              </Box>
              <IconButton
                size='small'
                onClick={removeFile}
                data-testid={removeFileTestId}
              >
                <CancelIcon
                  sx={{
                    fontSize: '20px',
                    color: sys.color.onSurfaceVariant,
                  }}
                />
              </IconButton>
            </Box>
          )}

          {/* Loading progress */}
          {uploadPending && file && (
            <Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mb: 1,
                }}
              >
                <Box display='flex' alignItems='center' gap={1}>
                  <MuiTypography variant='body2' color='text.secondary'>
                    Importing... {file.name}
                  </MuiTypography>
                </Box>
                <MuiTypography variant='body2' fontWeight='medium'>
                  {uploadProgress}%
                </MuiTypography>
              </Box>
              <MuiLinearProgress
                variant='determinate'
                value={uploadProgress}
                sx={{ height: 6, borderRadius: 1 }}
                aria-label={`Import progress: ${uploadProgress}%`}
                aria-valuenow={uploadProgress}
                aria-valuemin={0}
                aria-valuemax={100}
                data-testid={progressBarTestId}
              />
            </Box>
          )}

          {/* Copy and paste securities */}
          {!uploadPending && !file && (
            <Box
              display='flex'
              alignItems='center'
              justifyContent='center'
              gap={1}
              onClick={openPasteDialog}
              data-testid={copyPasteTestId}
              sx={{
                cursor: 'pointer',
                color: sys.color.onSurface,
              }}
            >
              <MuiTypography variant='body2'>
                {t('bulkImport:copyAndPaste')}
              </MuiTypography>
              <ContentPasteIcon
                sx={{
                  fontSize: '18px',
                }}
              />
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogFooter>
        <Box display='flex' justifyContent='end' gap={1}>
          <Button
            onClick={handleClose}
            variant='tonal'
            label={t('shared:cancel')}
            disabled={uploadPending}
            dataTestId={cancelButtonTestId}
          />
          <Button
            onClick={handleSubmit}
            label={importButtonLabel || 'Import file'}
            disabled={!file || uploadPending}
            loading={uploadPending}
            dataTestId={submitButtonTestId}
          />
        </Box>
      </DialogFooter>

      {/* Paste Dialog */}
      <Dialog
        open={showPasteDialog}
        onClose={closePasteDialog}
        maxWidth='sm'
        fullWidth
        data-testid={pasteDialogTestId}
      >
        <DialogTitle onClose={closePasteDialog}>
          {t('bulkImport:importCsvFile')}
        </DialogTitle>
        <DialogContent>
          <Box display='flex' flexDirection='column' gap={2}>
            <MuiTypography color='text.dark'>
              {t('bulkImport:pasteDescription')}
            </MuiTypography>
            <Box>
              <MuiTypography
                variant='body2'
                sx={{
                  mb: 1,
                  fontWeight: 500,
                  color: sys.color.onSurface,
                }}
              >
                {t('bulkImport:pasteCsv')}
              </MuiTypography>
              <TextField
                multiline
                rows={6}
                fullWidth
                value={pastedText}
                onChange={handlePastedTextChange}
                placeholder={t('bulkImport:pastePlaceholder')}
                variant='outlined'
                inputProps={{ 'data-testid': pasteDialogTextareaTestId }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: sys.color.surface,
                  },
                }}
              />
            </Box>
            <Box
              display='flex'
              alignItems='center'
              justifyContent='center'
              gap={0.5}
            >
              <Link
                onClick={downloadTemplate}
                data-testid={pasteDialogDownloadTemplateTestId}
                sx={{
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  textDecoration: 'none',
                  color: sys.color.onSurface,
                }}
              >
                <MuiTypography variant='body2'>
                  {t('bulkImport:downloadTemplate')}
                </MuiTypography>
                <DownloadIcon
                  sx={{
                    fontSize: '16px',
                  }}
                />
              </Link>
            </Box>
          </Box>
        </DialogContent>
        <DialogFooter>
          <Box display='flex' justifyContent='end' gap={1}>
            <Button
              onClick={closePasteDialog}
              variant='tonal'
              label={t('shared:cancel')}
              dataTestId={pasteDialogCancelTestId}
            />
            <Button
              onClick={handlePasteSubmit}
              label={t('shared:import')}
              disabled={!pastedText.trim()}
              dataTestId={pasteDialogImportTestId}
            />
          </Box>
        </DialogFooter>
      </Dialog>
    </Dialog>
  );
};

export default ImportCsvModal;
